if (!customElements.get('social-showcase')) {
  customElements.define(
    'social-showcase',
    class SocialShowcase extends HTMLElement {
      connectedCallback() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }

        this.items = Array.from(this.querySelectorAll('[data-parallax-item]'));
        if (!this.items.length) {
          return;
        }

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onIntersect = this.onIntersect.bind(this);

        this.isVisible = false;
        this.rafId = null;
        this.offsetStep = 26;

        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          threshold: 0,
        });

        this.observer.observe(this);
        window.addEventListener('resize', this.onResize);
      }

      onIntersect(entries) {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        this.isVisible = entry.isIntersecting;

        if (this.isVisible) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.scheduleUpdate();
        } else {
          window.removeEventListener('scroll', this.onScroll);
        }
      }

      onResize() {
        this.scheduleUpdate();
      }

      onScroll() {
        if (!this.isVisible) {
          return;
        }

        this.scheduleUpdate();
      }

      scheduleUpdate() {
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
          this.updateParallax();
        });
      }

      updateParallax() {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;

        const start = viewportHeight;
        const end = -rect.height;
        const rawProgress = (start - rect.top) / (start - end);
        const progress = Math.min(1, Math.max(0, rawProgress));

        const desktopMotion = {
          offsets: [-88, 88, -30, -88, 88],
          xOffsets: [0, 0, 0, 0, 0],
          baseScale: 0.86,
          scaleDelta: 0.14,
          centerBoost: 0.03,
        };
        const mobileMotion = {
          offsets: [-14, 14, 14, 14, -14],
          xOffsets: [-18, 0, 0, 0, 18],
          baseScale: 1,
          scaleDelta: 0,
          centerBoost: 0,
        };
        const motion = isDesktop ? desktopMotion : mobileMotion;

        this.items.forEach((item, index) => {
          const baseOffset = motion.offsets[index] ?? (index % 2 === 0 ? -this.offsetStep : this.offsetStep);
          const translateY = baseOffset * (1 - progress);
          const baseOffsetX = motion.xOffsets[index] ?? 0;
          const translateX = baseOffsetX * (1 - progress);
          let scale = motion.baseScale + motion.scaleDelta * progress;

          if (index === 2) {
            scale += motion.centerBoost * progress;
          }

          item.style.setProperty('--parallax-y', `${translateY.toFixed(2)}px`);
          item.style.setProperty('--parallax-x', `${translateX.toFixed(2)}px`);
          item.style.setProperty('--parallax-scale', scale.toFixed(4));
        });
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);

        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }
      }
    }
  );
}
